/**
 * School collection table (bulk order) UX: cart mutations with school line properties,
 * and client-side grid/table switching when both views are pre-rendered in the DOM.
 */

(function () {
  const ST_COLLECTION_VIEW_STORAGE_KEY = "st-collection-cv";
  const VAT_MULTIPLIER = 1.2;

  function lineTotalWithVat(line) {
    const base = Number(line?.final_line_price ?? 0);
    if (!Number.isFinite(base)) return 0;
    return line?.taxable ? Math.round(base * VAT_MULTIPLIER) : base;
  }

  function syncToggleButtons(buttons, mode) {
    buttons.forEach((btn) => {
      const active = btn.getAttribute("data-view-mode") === mode;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  function syncStCollectionViewToggleUi() {
    const root = document.querySelector("[data-collection-view-root]");
    if (!root) return;
    const mode = root.dataset.collectionViewMode || "";
    root.classList.toggle("st-collection-page--table", mode === "table");
    syncToggleButtons(
      root.querySelectorAll("[data-collection-view-button]"),
      mode
    );
  }

  function syncStProductViewToggleUi() {
    const root = document.querySelector("[data-product-view-root]");
    if (!root) return;
    const mode = root.dataset.productViewMode || "";
    syncToggleButtons(root.querySelectorAll("[data-product-view-button]"), mode);
  }

  window.syncStCollectionViewToggleUi = syncStCollectionViewToggleUi;

  if (!customElements.get("school-bulk-order")) {
    customElements.define(
      "school-bulk-order",
      class SchoolBulkOrder extends HTMLElement {
        constructor() {
          super();
          this._cartUnsubscriber = null;
          this._handleBulkChangeBound = this._handleBulkChange.bind(this);
          this._quantityChangeDebounced = debounce(
            this._quantityChangeCommitted.bind(this),
            350
          );
        }

        connectedCallback() {
          this.addEventListener("change", this._handleBulkChangeBound);
          this._cartUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            (payload) => {
              if (
                payload &&
                String(payload.source) === String(this.dataset.instanceId)
              ) {
                return;
              }
              this.syncFromCartFetch().catch(() => {});
            }
          );
          this.syncFromCartFetch().catch(() => {});
        }

        disconnectedCallback() {
          this.removeEventListener("change", this._handleBulkChangeBound);
          this._cartUnsubscriber?.();
          this._cartUnsubscriber = null;
        }

        _handleBulkChange(event) {
          if (event.target?.matches?.("[data-school-bulk-variant-select]")) {
            this.onVariantSelectChange(event);
            return;
          }
          if (event.target?.classList?.contains?.("quantity__input"))
            this._quantityChangeDebounced(event);
        }

        onVariantSelectChange(event) {
          const sel = event.target;
          const row = sel.closest("[data-school-bulk-product-row]");
          if (!row || !this.contains(row)) return;
          row.dataset.currentVariant = sel.value;
          this.syncBulkRowDisplayedMeta(row);
          this.fetchCartJson().then((cart) => this.applyCartToDom(cart));
        }

        syncBulkRowDisplayedMeta(row) {
          const sel = row.querySelector("[data-school-bulk-variant-select]");
          const opt = sel?.selectedOptions?.[0];
          if (!opt) return;

          const skuWrap = row.querySelector("[data-school-bulk-row-sku]");
          if (skuWrap) {
            skuWrap.textContent = "";
            const sku = opt.getAttribute("data-sku");
            if (sku) skuWrap.textContent = sku;
            else {
              const muted = document.createElement("span");
              muted.className = "st-school-bulk-order__muted";
              muted.textContent = "Not set";
              skuWrap.appendChild(muted);
            }
          }

          const centsRaw = Number(opt.dataset.priceCents ?? 0);
          const cents = Number.isFinite(centsRaw) ? centsRaw : 0;
          const unit = row.querySelector("[data-school-bulk-row-unit-money]");
          if (unit) unit.textContent = this.formatMoney(cents);

          const vid = String(sel.value);
          row.querySelectorAll("[data-variant-qty-shell]").forEach((shell) => {
            const shellVid = shell.getAttribute("data-variant-qty-shell");
            const on = shellVid === vid;
            if (on) {
              shell.removeAttribute("hidden");
              shell.removeAttribute("aria-hidden");
              shell.removeAttribute("tabindex");
            } else {
              shell.setAttribute("hidden", "");
              shell.setAttribute("aria-hidden", "true");
              shell.setAttribute("tabindex", "-1");
            }
          });

          const qCell = row.querySelector(".st-school-bulk-order__quantity-cell");
          if (qCell) {
            qCell.classList.toggle(
              "st-school-bulk-order__quantity-cell--sold-out",
              opt.dataset.available !== "true"
            );
          }
        }

        _quantityChangeCommitted(event) {
          if (!event.target?.classList?.contains("quantity__input")) return;
          const vid =
            event.target.dataset?.index ??
            event.target.id?.replace("Quantity-", "");
          if (!vid) return;
          this.applyVariantQuantity(String(vid), event.target.value);
        }

        get schoolLineProperties() {
          const id = this.dataset.schoolId;
          if (!id) return null;
          return {
            _school_id: id,
            _collection_handle: this.dataset.collectionHandle || "",
            _school_name: this.dataset.schoolName || "",
          };
        }

        formatMoney(subunitCents) {
          const code = (
            this.dataset.currencyCode ||
            "USD"
          ).toString();
          try {
            return new Intl.NumberFormat(document.documentElement.lang || "en", {
              style: "currency",
              currency: code,
            }).format(Number(subunitCents) / 100);
          } catch (_) {
            return `${(Number(subunitCents) / 100).toFixed(2)} ${code}`;
          }
        }

        fetchCartJson() {
          return fetch(`${routes.cart_url}.js`, {
            headers: { Accept: "application/json" },
          }).then((r) => r.json());
        }

        findSchoolLines(cart, variantId) {
          const schoolId = this.dataset.schoolId;
          if (!schoolId || !cart.items) return [];
          return cart.items.filter((item) => {
            const itemSchoolId =
              item.properties && item.properties._school_id != null
                ? String(item.properties._school_id)
                : "";
            return (
              item.variant_id === Number(variantId) && itemSchoolId === schoolId
            );
          });
        }

        sourceSkuForVariant(variantId) {
          const option = this.querySelector(
            `[data-school-bulk-variant-select] option[value="${variantId}"]`
          );
          const sku = option?.dataset?.sourceSku;
          return sku ? String(sku) : "";
        }

        lineProperties(variantId) {
          const properties = { ...(this.schoolLineProperties || {}) };
          const sourceSku = this.sourceSkuForVariant(variantId);
          if (sourceSku) properties._source_sku = sourceSku;
          return Object.keys(properties).length ? properties : null;
        }

        addSchoolLine(variantId, quantity) {
          const body = JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity,
                properties: this.lineProperties(variantId),
              },
            ],
          });
          return fetch(`${routes.cart_add_url}`, {
            ...fetchConfig(),
            body,
          }).then((r) => r.json());
        }

        changeSchoolLine(lineKey, quantity) {
          const body = JSON.stringify({ id: lineKey, quantity });
          return fetch(`${routes.cart_change_url}`, {
            ...fetchConfig(),
            body,
          }).then((r) => r.json());
        }

        async applyVariantQuantity(variantId, rawQuantity) {
          const parsed = parseInt(rawQuantity, 10);
          const qty = Number.isNaN(parsed) ? 0 : parsed;

          const input = document.getElementById(`Quantity-${variantId}`);
          if (!input || !this.contains(input)) return;

          const row = input.closest("[data-school-bulk-product-row]");
          if (!row) return;

          const minAllowed = parseInt(input?.min ?? "0", 10);
          let nextQty = Math.max(qty, minAllowed);
          if (input?.max !== undefined && input.max !== "") {
            nextQty = Math.min(nextQty, parseInt(input.max, 10));
          }

          row.classList.add("is-updating");
          this.clearMessages();

          try {
            const cart = await this.fetchCartJson();
            const schoolLines = this.findSchoolLines(cart, variantId);
            const currentQty = schoolLines.reduce(
              (t, line) => t + line.quantity,
              0
            );

            if (schoolLines.length === 0 && nextQty > 0) {
              await this.addSchoolLine(variantId, nextQty);
            } else if (schoolLines.length > 0 && currentQty !== nextQty) {
              for (const [index, line] of schoolLines.entries()) {
                await this.changeSchoolLine(line.key, index === 0 ? nextQty : 0);
              }
            }

            const fresh = await this.fetchCartJson();
            this.applyCartToDom(fresh);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: this.dataset.instanceId,
              cartData: fresh,
            });
          } catch (_) {
            this.setInlineError(window.cartStrings?.error || "");
            await this.syncFromCartFetch();
          } finally {
            row.classList.remove("is-updating");
          }
        }

        clearMessages() {
          const ok = this.querySelector("[data-school-bulk-message]");
          const er = this.querySelector("[data-school-bulk-error]");
          if (ok) ok.textContent = "";
          if (er) er.textContent = "";
        }

        setInlineError(msg) {
          const er = this.querySelector("[data-school-bulk-error]");
          if (er) er.textContent = msg || "";
        }

        applyCartToDom(cart) {
          const schoolId = this.dataset.schoolId;
          if (!schoolId || !cart.items) return;

          let schoolQty = 0;
          let schoolSubtotal = 0;
          for (const item of cart.items) {
            const sid =
              item.properties && item.properties._school_id != null
                ? String(item.properties._school_id)
                : "";
            if (sid === schoolId) {
              schoolQty += item.quantity;
              schoolSubtotal += lineTotalWithVat(item);
            }
          }

          this.querySelectorAll("[data-school-bulk-product-row]").forEach(
            (row) => {
              row.querySelectorAll(".quantity__input").forEach((qi) => {
                const vid =
                  qi.dataset?.index ?? qi.id?.replace("Quantity-", "");
                if (!vid) return;
                const lines = this.findSchoolLines(cart, vid);
                const q = lines.reduce((t, l) => t + l.quantity, 0);
                qi.value = String(q);
                qi.setAttribute("data-cart-quantity", String(q));
              });

              const sel = row.querySelector("[data-school-bulk-variant-select]");
              if (!sel) return;
              const variantId = sel.value;
              const lines = this.findSchoolLines(cart, variantId);
              const lineTotal = lines.reduce(
                (t, l) => t + lineTotalWithVat(l),
                0
              );
              const totalEl = row.querySelector("[data-school-bulk-row-total]");
              if (totalEl)
                totalEl.textContent = this.formatMoney(lineTotal);
            }
          );

          this.querySelectorAll("[data-school-bulk-product-row]").forEach(
            (row) => {
              this.syncBulkRowDisplayedMeta(row);
            }
          );

          const countEl = this.querySelector("[data-school-bulk-summary-count]");
          const subEl = this.querySelector("[data-school-bulk-summary-subtotal]");
          if (countEl) countEl.textContent = String(schoolQty);
          if (subEl) subEl.textContent = this.formatMoney(schoolSubtotal);

          const checkout = this.querySelector("[data-school-bulk-checkout]");
          if (checkout) checkout.disabled = schoolQty === 0;
        }

        async syncFromCartFetch() {
          const cart = await this.fetchCartJson();
          this.applyCartToDom(cart);
        }
      }
    );
  }

  function persistCollectionViewPreference(mode) {
    try {
      sessionStorage.setItem(ST_COLLECTION_VIEW_STORAGE_KEY, mode);
    } catch (_) {}
  }

  function applyLocalCollectionViewMode(mode, options = {}) {
    const { skipPersist = false } = options;
    const root = document.querySelector("[data-collection-view-root]");
    const mount = document.getElementById("product-grid");
    if (!root || !mount) return false;
    const panes = mount.querySelectorAll("[data-st-pane]");
    if (panes.length < 2) return false;

    const resolved = mode === "table" ? "table" : "grid";
    panes.forEach((pane) => {
      const key = pane.getAttribute("data-st-pane");
      const show = key === resolved;
      if (show) {
        pane.removeAttribute("hidden");
        pane.setAttribute("aria-hidden", "false");
      } else {
        pane.setAttribute("hidden", "");
        pane.setAttribute("aria-hidden", "true");
      }
    });

    root.dataset.collectionViewMode = resolved;
    window.syncStCollectionViewToggleUi?.();

    if (!skipPersist) persistCollectionViewPreference(resolved);

    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(document);
    }

    return true;
  }

  function hydrateCollectionViewPreference() {
    const root = document.querySelector("[data-collection-view-root]");
    const mount = document.getElementById("product-grid");
    if (!root || !mount || !mount.querySelector("[data-st-pane]")) return;

    try {
      const stored = sessionStorage.getItem(ST_COLLECTION_VIEW_STORAGE_KEY);
      if (stored !== "grid" && stored !== "table") return;
      const current = root.dataset.collectionViewMode || "";
      if (stored === current) return;
      applyLocalCollectionViewMode(stored, { skipPersist: true });
    } catch (_) {}
  }

  function initCollectionViewToggle() {
    const root = document.querySelector("[data-collection-view-root]");
    const buttons = root?.querySelectorAll("[data-collection-view-button]");
    const mount = document.getElementById("product-grid");
    if (!root || !buttons?.length || !mount?.querySelector("[data-st-pane]"))
      return;

    hydrateCollectionViewPreference();

    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const requested = btn.getAttribute("data-view-mode");
        if (!requested) return;
        const current = root.dataset.collectionViewMode || "grid";
        if (requested === current) return;
        btn.closest(".st-view-toggle")?.setAttribute("aria-busy", "true");
        applyLocalCollectionViewMode(requested);
        btn.closest(".st-view-toggle")?.removeAttribute("aria-busy");
      });
    });
  }

  function applyLocalProductViewMode(mode) {
    const root = document.querySelector("[data-product-view-root]");
    if (!root) return false;
    const panes = root.querySelectorAll("[data-st-product-pane]");
    if (!panes.length) return false;

    const resolved = mode === "table" ? "table" : "grid";
    panes.forEach((pane) => {
      const key = pane.getAttribute("data-st-product-pane");
      const show = key === resolved;
      if (show) {
        pane.removeAttribute("hidden");
        pane.setAttribute("aria-hidden", "false");
      } else {
        pane.setAttribute("hidden", "");
        pane.setAttribute("aria-hidden", "true");
      }
    });

    root.dataset.productViewMode = resolved;
    syncStProductViewToggleUi();

    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(document);
    }

    return true;
  }

  function initProductViewToggle() {
    const root = document.querySelector("[data-product-view-root]");
    const buttons = root?.querySelectorAll("[data-product-view-button]");
    if (!root || !buttons?.length || !root.querySelector("[data-st-product-pane]"))
      return;

    syncStProductViewToggleUi();

    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        const requested = btn.getAttribute("data-view-mode");
        if (!requested) return;
        const current = root.dataset.productViewMode || "grid";
        if (requested === current) return;
        btn.closest(".st-view-toggle")?.setAttribute("aria-busy", "true");
        applyLocalProductViewMode(requested);
        btn.closest(".st-view-toggle")?.removeAttribute("aria-busy");
      });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    initCollectionViewToggle();
    initProductViewToggle();
  });
})();
